import classnames from 'classnames';
import sum from 'lodash/sum';
import { bool, func, object } from 'prop-types';
import React from 'react';
import { Table } from 'reactstrap';

import { getSpentBudgetForProduct } from '../../campaign-editor/utils';
import CurrencyFormat from '../../generic/CurrencyFormat';
import CurrencyInput from '../../generic/CurrencyInput';
import getInfoTooltip from '../../generic/Tooltip/get-info-tooltip';
import { campaignShape } from '../../prop-types';
import {
  calculateBudgetFromNewValues,
  calculateBudgetFromOldValues,
} from '../change-product-budget/utils';
import HELP_TEXTS from '../HELP_TEXTS';
import {
  BUDGET_FIELDS,
  productAggregatesMapShape,
  WRITABLE_BUDGET_FIELDS,
} from './constants';
import TransferBudgetTotals from './TransferBudgetTotals';
import { calculateTotalDelta, checkIfDeltasMatch } from './utils';

function TransferBudgetTable({
  productAggregatesMap,
  targetProductAggregatesMap,
  onChange,
  campaign,
  targetCampaign,
  sourceCampaign,
  otherDeltas,
}) {
  const productAggregates = Object.values(productAggregatesMap);
  const totalNewBudget = sum(
    productAggregates.map(productAggregate =>
      calculateBudgetFromNewValues(productAggregate, campaign),
    ),
  );
  const totalOldBudget = sum(
    productAggregates.map(productAggregate =>
      calculateBudgetFromOldValues(productAggregate, campaign),
    ),
  );

  const targetProductAggregates =
    targetProductAggregatesMap && Object.values(targetProductAggregatesMap);
  const totalTargetNewBudget =
    targetProductAggregatesMap &&
    sum(
      targetProductAggregates.map(productAggregate =>
        calculateBudgetFromNewValues(productAggregate, targetCampaign),
      ),
    );
  const totalTargetOldBudget =
    targetProductAggregatesMap &&
    sum(
      targetProductAggregates.map(productAggregate =>
        calculateBudgetFromOldValues(productAggregate, targetCampaign),
      ),
    );
  const totalTargetDelta =
    targetProductAggregates && totalTargetNewBudget - totalTargetOldBudget;
  const allBudgets = Object.values(BUDGET_FIELDS);
  const deltaType = sourceCampaign ? 'sourceDelta' : 'targetDelta';

  return (
    <Table className="table-md table-va-middle mt-1 mb-5 product-budget">
      <thead className="bg-gray-200">
        <tr>
          <th className="border-top-0">Products</th>
          <th className="border-top-0 text-nowrap">Mngmt. Fee</th>
          <th colSpan={3} className="text-center border-top-0">
            Net Client Budget External
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Net Client Budget WKZ
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Free Service
          </th>
          <th colSpan={3} className="text-center border-top-0">
            Co Invest
          </th>
          <th colSpan={2} className="text-center border-top-0">
            <div className="d-flex align-items-center justify-content-center">
              <span>Budget</span>
              {getInfoTooltip(HELP_TEXTS, 'totalBudget', 'transferBudget')}
            </div>
          </th>
          <th className="border-top-0 text-align text-danger">Spent</th>
          <th className="border-top-0 text-center text-success">Left</th>
          <th className="border-top-0 text-center text-info">
            <div className="d-flex align-items-center justify-content-center">
              <span>Delta</span>
              {getInfoTooltip(HELP_TEXTS, deltaType, 'transferBudget')}
            </div>
          </th>
        </tr>
        <tr>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th className="text-center text-info">Delta</th>
          <th className="text-center">Current</th>
          <th className="text-center">New</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(productAggregatesMap).map(([id, productAggregate]) => {
          const { adjustedFee, newValues, oldValues, product } =
            productAggregate;

          const spentAmount = getSpentBudgetForProduct(campaign, product);

          const oldBudget = calculateBudgetFromOldValues(
            productAggregate,
            campaign,
          );
          const newBudget = calculateBudgetFromNewValues(
            productAggregate,
            campaign,
          );
          return (
            <tr key={id}>
              <td>{productAggregate.label}</td>
              <td className="text-right">
                {adjustedFee ? (
                  <CurrencyFormat
                    name={`${id}-adjustedFee`}
                    value={adjustedFee * 100}
                    prefix=""
                    suffix=" %"
                    text
                  />
                ) : (
                  '-'
                )}
              </td>
              {allBudgets.map(field => {
                const delta = newValues[field] - oldValues[field];
                const totalDelta = calculateTotalDelta(
                  productAggregates,
                  field,
                );

                const areDeltasMatching = checkIfDeltasMatch(
                  totalDelta,
                  otherDeltas,
                  field,
                );

                return WRITABLE_BUDGET_FIELDS.includes(field)
                  ? [
                      <td
                        key={`old-${field}`}
                        className="text-right bg-gray-200"
                      >
                        <CurrencyFormat
                          name={`productAggregates.${id}.oldValues.${field}`}
                          value={oldValues[field]}
                          className="text-nowrap"
                          text
                        />
                      </td>,
                      <td
                        key={`new-${field}`}
                        className="text-right bg-gray-200"
                      >
                        <CurrencyInput
                          name={`productAggregates.${id}.newValues.${field}`}
                          value={newValues[field]}
                          onChange={onChange}
                          className="wider-input"
                        />
                      </td>,
                      <td
                        key={`delta-${field}`}
                        className={classnames('text-right bg-gray-200', {
                          'cr-border-red':
                            (sourceCampaign && delta > 0) ||
                            (delta !== 0 && !areDeltasMatching),
                          'cr-border-green':
                            delta !== 0 &&
                            areDeltasMatching &&
                            !(sourceCampaign && delta > 0),
                        })}
                      >
                        <CurrencyFormat
                          name={`${id}.deltaAmount.${field}`}
                          value={delta}
                          className="text-nowrap text-info"
                          text
                        />
                      </td>,
                    ]
                  : [
                      <td className="text-right bg-gray-200">
                        <CurrencyFormat
                          name={`productAggregates.${id}.oldValues.${field}`}
                          value={oldValues[field]}
                          className="text-nowrap"
                          text
                        />
                      </td>,
                    ];
              })}
              <td className="text-right bg-gray-200">
                <CurrencyFormat
                  name={`${id}-oldBudget`}
                  value={oldBudget}
                  className="text-nowrap"
                  text
                />
              </td>
              <td className="text-right bg-gray-200">
                <CurrencyFormat
                  name={`${id}-newBudget`}
                  value={newBudget}
                  className={classnames('text-nowrap', {
                    'bg-danger text-white': newBudget < spentAmount,
                  })}
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-spentAmount`}
                  value={spentAmount}
                  className="text-nowrap text-danger"
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-leftAmount`}
                  value={newBudget - spentAmount}
                  className={classnames('text-nowrap text-success', {
                    'text-danger': newBudget - spentAmount < 0,
                  })}
                  text
                />
              </td>
              <td className="text-right">
                <CurrencyFormat
                  name={`${id}-deltaAmount`}
                  value={newBudget - oldBudget}
                  className="text-nowrap text-info"
                  text
                />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <TransferBudgetTotals
          sourceCampaign={sourceCampaign}
          campaign={campaign}
          productAggregatesMap={productAggregatesMap}
          totalNewBudget={totalNewBudget}
          totalOldBudget={totalOldBudget}
          totalTargetDelta={totalTargetDelta}
          onChange={onChange}
          otherDeltas={otherDeltas}
        />
      </tfoot>
    </Table>
  );
}

TransferBudgetTable.propTypes = {
  onChange: func.isRequired,
  campaign: campaignShape.isRequired,
  productAggregatesMap: productAggregatesMapShape.isRequired,
  sourceCampaign: bool,
  targetProductAggregatesMap: productAggregatesMapShape,
  targetCampaign: campaignShape,
  otherDeltas: object, // eslint-disable-line react/forbid-prop-types
};

TransferBudgetTable.defaultProps = {
  sourceCampaign: false,
  otherDeltas: undefined,
  targetProductAggregatesMap: undefined,
  targetCampaign: undefined,
};

export default TransferBudgetTable;
